import { useMemo } from "react";

import { useAddEmailToWorkMutation } from "../../../store/services/b2bMailService";
import { MyCustomerApiResponse } from "../../../store/services/customerService";
import {
  CustomerEmailMatch,
  EmailMatch,
  ProcessedEmail,
} from "../types";
import { CustomerCommunication } from "../../../store/types/communication.types";

export const useEmailMatching = (
  emails: ProcessedEmail[],
  customers: MyCustomerApiResponse[]
) => {
  const [addToWork, { isLoading }] = useAddEmailToWorkMutation();

  // Extract all unique email addresses from customer communications
  const customerEmails = useMemo(() => {
    const emailMap = new Map<string, CustomerEmailMatch>();

    customers.forEach((customer) => {
      customer.Communications.forEach((comm: CustomerCommunication) => {
        if (comm.Type === 3) {
          // Email type
          emailMap.set(comm.Value!.toLowerCase(), {
            customer: customer.Name,
            customerNo: customer.Id.toString(),
            communicationNo: comm.CommunicationNo,
            contactPerson: comm.ContactPerson?.Firstname
              ? `${comm.ContactPerson.Firstname} ${comm.ContactPerson.Lastname}`
              : null,
          });
        }
      });
    });

    return emailMap;
  }, [customers]);

  // Find potential matches for each email
  const emailMatches = useMemo(() => {
    return emails.map((email) => {
      const fromAddress = email.from?.emailAddress.address.toLowerCase();
      const toAddresses = email.toRecipients?.map((r) =>
        r.emailAddress.address.toLowerCase()
      );

      if (!fromAddress || !toAddresses) {
        return email;
      }
      const matches: EmailMatch[] = [];

      // Check from address
      if (customerEmails.has(fromAddress)) {
        const matchData = customerEmails.get(fromAddress);
        if (matchData) {
          matches.push({
            type: "from",
            ...matchData,
          });
        }
      }

      // Check to addresses
      toAddresses.forEach((address) => {
        if (customerEmails.has(address)) {
          const matchData = customerEmails.get(address);
          if (matchData) {
            matches.push({
              type: "to",
              ...matchData,
            });
          }
        }
      });
      console.log("matches", email);
      return {
        ...email,
        matches,
      } as ProcessedEmail;
    });
  }, [emails, customerEmails]);

  const confirmMatch = async (
    emailId: string,
    workId?: number,
    eventDataId?: number
  ) => {
    const emailMatch = emails.find((m) => m.id === emailId);
    if (!emailMatch || !workId) {
      return;
    }
    await addToWork({
      eventDataId,
      type: "from",
      email: {
        Id: emailMatch.id,
        Subject: emailMatch.subject,
        ConversationId: emailMatch.conversationId,
        Body: {
          Content: emailMatch.body.content,
          ContentType: emailMatch.body.contentType,
        },
        From: {
          EmailAddress: {
            Address: emailMatch.from?.emailAddress.address ?? "",
            Name: emailMatch.from?.emailAddress.name ?? "",
          },
        },
        ToRecipients: (emailMatch.toRecipients ?? []).map((r) => ({
          EmailAddress: {
            Address: r.emailAddress.address,
            Name: r.emailAddress.name,
          },
        })),
        SentDateTime: emailMatch.receivedDateTime,
        HasAttachments: emailMatch.hasAttachments,
        BodyPreview: "",
        ChangeKey: "",
        CreatedDateTime: emailMatch.receivedDateTime,
        IsRead: false,
        LastModifiedDateTime: emailMatch.receivedDateTime,
        ReceivedDateTime: emailMatch.receivedDateTime,
      },
      workNo: workId,
    })
      .unwrap()
      .then(() => {
        console.log("Email added to work");
      });
  };

  return {
    emailMatches,
    confirmMatch,
    isLoading,
  };
};
